import React, { FC } from 'react';
import cn from 'classnames';
import TooltipPoratal from 'components/UI/TooltipPoratal';
import { ReactComponent as CampFilterSvg } from 'assets/campFilter.svg';
import { createPortal } from 'react-dom';
import DateRangeFilter from 'components/DateRangeFilter';
import { parsingDate } from 'utils/momentParse';
import moment from 'moment';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import Button from 'components/UI/Buttons/buttons';
import { useCalculatePosition } from 'domains/ReportBuilder/hooks/useCalculatePosition';
import {
  IReportBuilderFormik,
  TGroupingItem,
  TGroupingsState,
  TObjectionTypes,
  TSelectOptionsType,
} from 'domains/ReportBuilder/types';
import { FormikProps } from 'formik';
import css from '../../styles.module.scss';
import Groupings from './Groupings';
import ReportFilters from './ReportFilters';
import Statistics from './Statistics';
import { THeaderProprs } from '../Header/types';

type TCompositeFilterProprs = {
  formik: FormikProps<IReportBuilderFormik>;
  groupings: TGroupingsState;
  filterings: TGroupingsState;
  objections: Record<TObjectionTypes, TGroupingItem>;
  statistics: TSelectOptionsType[];
  currentUserStep: TObjectionTypes;
  isLoading: boolean;
  isGlobalLoading: boolean;
  setObjections: React.Dispatch<
    React.SetStateAction<Record<TObjectionTypes, TGroupingItem>>
  >;
  /** Фильтр не заполнен */
  emptyFilter: THeaderProprs['emptyFilter'];
  resetForm: () => void;
};

const CompositeFilter: FC<TCompositeFilterProprs> = ({
  formik,
  groupings,
  filterings,
  objections,
  statistics,
  currentUserStep,
  isLoading,
  isGlobalLoading,
  setObjections,
  emptyFilter,
  resetForm,
}) => {
  const {
    t,
    buttonRef,
    toggle,
    isOpen,
    panelRef,
    position,
    handleFirstInteraction,
    shouldShowHints,
    tabPanelsRef,
    showBottomFade,
    selectedTab,
    setIsOpen,
    handleTabSelect,
  } = useCalculatePosition();

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        onClick={() => {
          if (isLoading) return;
          toggle();
        }}
        className={cn(css.actions_btnWrapper, {
          _isDisable: isLoading,
        })}
        data-filter-interactive
      >
        <TooltipPoratal
          hint={t('filter.customize')}
          classname={cn(css.actions_btn, {
            _isEmpty: emptyFilter,
            _isSelected: !emptyFilter,
            _isDisable: isLoading,
          })}
          white
          isDisabled={isLoading}
        >
          <CampFilterSvg />
        </TooltipPoratal>
      </button>
      {createPortal(
        <div
          data-filter-interactive
          ref={panelRef}
          className={cn(css.filters, {
            _isOpen: isOpen,
          })}
          style={{
            top: position.top,
            left: position.left,
          }}
        >
          <div
            className={css.filters__body}
            onMouseDown={handleFirstInteraction}
          >
            <div className={css.period}>
              <span className={css.period__label}>
                {t(`campaigns_page.campaign_settings.Period`)}
              </span>
              <DateRangeFilter
                range={formik.values.period}
                dateStart={parsingDate(moment().subtract(365, 'days')).toDate()}
                dateEnd={parsingDate(new Date()).toDate()}
                onSetDateRange={(period) =>
                  formik.setFieldValue('period', period)
                }
                hideAllTime
              />
            </div>
            <Tabs
              selectedIndex={selectedTab}
              onSelect={handleTabSelect}
              forceRenderTabPanel
              className={cn(css.tabs, 'react-tabs')}
            >
              <div className={css.main}>
                <div className={css.main__tabs}>
                  <div
                    className={cn(css.hint, css.hint__tabs, {
                      _isShow: shouldShowHints,
                    })}
                  >
                    {t(`reportbuilder.applyFilters`)}
                  </div>
                  <TabList className={cn(css.tablist, 'react-tabs__tab-list')}>
                    <Tab>{t(`reportbuilder.groups`)}</Tab>
                    <Tab>{t(`analysis_page.filters_title`)}</Tab>
                    <Tab>{t(`users_page.navigation.statistics`)}</Tab>
                  </TabList>
                </div>
                <div className={css.main__tabpanels} ref={tabPanelsRef}>
                  <TabPanel>
                    <Groupings
                      formik={formik}
                      groupBy={formik.values.reportFilters.groupBy}
                      groupings={groupings}
                    />
                  </TabPanel>
                  <TabPanel>
                    <ReportFilters
                      containerRef={tabPanelsRef}
                      formik={formik}
                      filterBy={formik.values.reportFilters.filterBy}
                      objectionBy={formik.values.reportFilters.objectionBy}
                      filterings={filterings}
                      objections={objections}
                      setObjections={setObjections}
                      stateOfPanel={isOpen}
                      currentUserStep={currentUserStep}
                    />
                  </TabPanel>
                  <TabPanel>
                    <Statistics
                      statisticsBy={formik.values.reportFilters.statisticBy}
                      statistics={statistics}
                      setFieldValue={formik.setFieldValue}
                    />
                  </TabPanel>
                </div>
                <div
                  className={cn(css.main__actions, {
                    _isShowBlur: showBottomFade,
                  })}
                >
                  <div
                    className={cn(css.hint, css.hint__tabs, {
                      _isShow: shouldShowHints,
                    })}
                  >
                    {t(`reportbuilder.applyBtnInfo`)}
                  </div>

                  <Button
                    title={t(`clean_btn`)}
                    lightgrey
                    buttonAttributes={{
                      onClick: () => resetForm(),
                    }}
                  />
                  <Button
                    title={t(`apply_btn`)}
                    buttonAttributes={{
                      disabled:
                        !formik.values.reportFilters.objectionBy[
                          currentUserStep
                        ] ||
                        !Object.values(
                          formik.values.reportFilters.statisticBy,
                        ).some((value) => value === true) ||
                        isGlobalLoading,
                      onClick: () => {
                        formik.submitForm();
                        setIsOpen(false);
                      },
                    }}
                  />
                </div>
              </div>
            </Tabs>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default React.memo(CompositeFilter);
